import { useState } from 'react'
import { Dialog, DialogPanel } from '@headlessui/react'
import { XMarkIcon } from '@heroicons/react/24/outline'
import {
  ArrowRightIcon,
  CheckCircleIcon,
  ClockIcon,
  ShieldCheckIcon,
  CurrencyDollarIcon,
  DocumentCheckIcon,
  MagnifyingGlassIcon,
  DevicePhoneMobileIcon,
  BanknotesIcon,
} from '@heroicons/react/24/outline'
import svklockvarderingLogo from '../src/värdering.png'
import foretag from '../src/värdering-iphone.jpg'

const navigation = [
  { name: 'Kronos köpskydd', href: '/kronos-secure' },
  { name: 'Kronos Certified', href: '/kronos-certified' },
  { name: 'Kontrollera certifiering', href: '/kronos-certified-control' },
  { name: 'Klockvärdering', href: '/klockvardering' },
  { name: 'Företagsannonsering', href: '/foretagsannonsering' },
  { name: 'Marknadsföring', href: '/marknadsforing' },
  { name: 'Blogg', href: '/blogg' },
  { name: 'Integritetspolicy', href: '/integritetspolicy' },
  { name: 'Användarvillkor', href: '/anvandarvillkor' },
]

const benefits = [
  { icon: ClockIcon, title: 'Svar inom 24 timmar', text: 'Snabb bedömning av din klocka' },
  { icon: ShieldCheckIcon, title: 'Expertbedömning', text: 'Utförd av Svensk Klockvärdering' },
  { icon: DocumentCheckIcon, title: 'Skriftlig rapport', text: 'Underlag för dina behov' },
]

const steps = [
  {
    title: 'Logga in på Kronos',
    text: 'Börja med att logga in på ditt Kronos-konto.',
    icon: DevicePhoneMobileIcon,
  },
  {
    title: 'Beskriv din klocka',
    text: 'Fyll i uppgifterna som behövs för värderingen.',
    icon: DocumentCheckIcon,
  },
  {
    title: 'Betala enkelt med Swish',
    text: 'Granska beställningen och slutför betalningen.',
    icon: BanknotesIcon,
  },
  {
    title: 'Få din värdering',
    text: 'Svensk Klockvärdering analyserar uppgifterna och skickar rapporten inom 24 timmar.',
    icon: MagnifyingGlassIcon,
  },
]

function Klockvardering({ siteLogo }) {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false)

  return (
    <div className="min-h-screen bg-white text-slate-900">
      <header className="fixed inset-x-0 top-0 z-50 bg-white shadow">
        <nav className="flex items-center justify-between p-6 lg:px-8">
          <a href="/" className="flex items-center -m-1.5 p-1.5">
            <span className="sr-only">Krono</span>
            <img alt="Krono Logo" src={siteLogo} className="h-6 w-auto rounded-md" />
          </a>

          <div className="flex lg:hidden">
            <button
              type="button"
              className="p-2.5 text-gray-700 rounded-md"
              onClick={() => setMobileMenuOpen(true)}
            >
              <span className="sr-only">Open menu</span>
              <svg
                className="h-6 w-6"
                fill="none"
                viewBox="0 0 24 24"
                strokeWidth="1.5"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M3.75 6.75h16.5M3.75 12h16.5m-16.5 5.25h16.5"
                />
              </svg>
            </button>
          </div>

          <div className="hidden lg:flex lg:gap-x-8">
            {navigation.map((item) => (
              <a
                key={item.name}
                href={item.href}
                className="text-sm font-semibold text-gray-900 hover:underline"
              >
                {item.name}
              </a>
            ))}
          </div>
        </nav>

        {/* Mobile menu */}
        <Dialog open={mobileMenuOpen} onClose={setMobileMenuOpen} className="lg:hidden">
          <div className="fixed inset-0 z-50" />
          <DialogPanel className="fixed inset-y-0 right-0 z-50 w-full max-w-sm bg-white px-6 py-6 overflow-y-auto shadow-lg">
            <div className="flex items-center justify-between">
              <button
                type="button"
                onClick={() => setMobileMenuOpen(false)}
                className="p-2.5 text-gray-700 rounded-md"
              >
                <span className="sr-only">Close menu</span>
                <XMarkIcon className="h-6 w-6" aria-hidden="true" />
              </button>
            </div>
            <div className="mt-6 space-y-2">
              {navigation.map((item) => (
                <a
                  key={item.name}
                  href={item.href}
                  className="block rounded-lg px-3 py-2 text-base font-semibold text-gray-900 hover:bg-gray-50"
                >
                  {item.name}
                </a>
              ))}
            </div>
          </DialogPanel>
        </Dialog>
      </header>

      <main className="">
        <section className="relative isolate overflow-hidden border-b border-slate-100 bg-white">
          <div className="pointer-events-none absolute -right-24 -top-28 h-96 w-96 rounded-full  blur-3xl" />
          <div className="relative mx-auto grid max-w-7xl items-center gap-10 px-5 py-14 sm:py-20 lg:grid-cols-2 lg:gap-16 lg:px-8 lg:py-24">
            <div className="text-center lg:text-left">
              <div className="mb-6 inline-flex items-center gap-2 rounded-full border border-sky-200 bg-white px-4 py-2 text-xs font-bold uppercase tracking-[0.16em] text-sky-700 shadow-sm">
                <ShieldCheckIcon className="h-4 w-4" /> Professionell klockvärdering
              </div>
              <h1 className="font-poppins text-4xl font-bold leading-tight tracking-tight text-slate-950 sm:text-5xl lg:text-6xl">
                Få din klocka <span className="text-sky-600">värderad</span> av experter
              </h1>
              <p className="mx-auto mt-6 max-w-xl text-base leading-7 text-slate-600 sm:text-lg lg:mx-0">
                Beställ en distansvärdering direkt via Kronos. Svensk Klockvärdering hjälper dig att
                få en professionell bedömning av klockans skick och marknadsvärde.
              </p>
              <div className="mt-8 flex flex-col items-stretch justify-center gap-3 sm:flex-row sm:items-center lg:justify-start">
                <a
                  href="#sa-fungerar-det"
                  className="inline-flex items-center justify-center gap-2 rounded-xl bg-sky-600 px-6 py-3.5 text-sm font-bold text-white shadow-lg shadow-sky-600/20 transition hover:-translate-y-0.5 hover:bg-sky-700"
                >
                  Så fungerar det <ArrowRightIcon className="h-4 w-4" />
                </a>
                <a
                  href="mailto:info@kronos.se"
                  className="inline-flex items-center justify-center rounded-xl border border-slate-300 bg-white px-6 py-3.5 text-sm font-bold text-slate-800 transition hover:border-sky-300 hover:bg-sky-50"
                >
                  Kontakta Kronos
                </a>
              </div>
              <div className="mt-8 flex flex-col items-center gap-3 text-sm text-slate-500 sm:flex-row sm:justify-center lg:justify-start">
                <span className="font-medium text-base">I samarbete med</span>
                <img
                  src={svklockvarderingLogo}
                  alt="Svensk Klockvärdering"
                  className="h-32 w-auto object-contain"
                />
              </div>
            </div>
            <div className="relative mx-auto w-full max-w-xl">
              <div className="absolute -inset-4 rounded-[2rem] bg-gradient-to-br from-sky-100 to-slate-100 blur-xl" />
              <div className="relative overflow-hidden rounded-[1.75rem] border border-white bg-white p-3 shadow-2xl shadow-slate-900/10">
                <img
                  src={foretag}
                  alt="Exempel på digital klockvärdering via Kronos"
                  className="max-h-[520px] w-full rounded-2xl object-contain object-center"
                />
                <div className="mx-2 mb-2 mt-3 flex items-center gap-3 rounded-2xl border border-sky-100 bg-sky-50/80 p-4">
                  <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-white text-sky-600 shadow-sm">
                    <ClockIcon className="h-6 w-6" />
                  </div>
                  <div>
                    <p className="text-sm font-bold text-slate-900">Värdering inom 24 timmar</p>
                    <p className="mt-0.5 text-xs text-slate-600">
                      En smidig process direkt via Kronos
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="mx-auto grid max-w-7xl gap-4 px-5 py-8 sm:grid-cols-3 lg:px-8 lg:py-10">
          {benefits.map(({ icon: Icon, title, text }) => (
            <div
              key={title}
              className="flex items-center gap-4 rounded-2xl border border-slate-200 bg-white p-5 transition hover:border-sky-200 hover:shadow-md"
            >
              <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-sky-50 text-sky-600">
                <Icon className="h-6 w-6" />
              </div>
              <div>
                <h2 className="text-sm font-bold text-slate-900">{title}</h2>
                <p className="mt-1 text-sm text-slate-500">{text}</p>
              </div>
            </div>
          ))}
        </section>

        <section className="bg-slate-50/80 py-16 sm:py-20">
          <div className="mx-auto max-w-5xl px-5 lg:px-8">
            <div className="mx-auto mb-10 max-w-2xl text-center">
              <p className="text-xs font-bold uppercase tracking-[0.18em] text-sky-700">
                En värdering för flera behov
              </p>
              <h2 className="mt-3 font-poppins text-3xl font-bold tracking-tight text-slate-950 sm:text-4xl">
                Få bättre koll på klockans värde
              </h2>
              <p className="mt-4 leading-7 text-slate-600">
                En professionell värdering kan vara ett värdefullt underlag när du ska sälja, se
                över din försäkring eller förstå klockans marknadsvärde.
              </p>
            </div>
            <div className="grid gap-4 sm:grid-cols-3">
              {[
                {
                  title: 'Försäljning',
                  text: 'Få ett bättre beslutsunderlag inför en försäljning.',
                  icon: CurrencyDollarIcon,
                },
                {
                  title: 'Försäkring',
                  text: 'Använd värderingen som underlag för försäkringsärenden.',
                  icon: ShieldCheckIcon,
                },
                {
                  title: 'Marknadsvärde',
                  text: 'Få en professionell bedömning av klockans värde.',
                  icon: MagnifyingGlassIcon,
                },
              ].map(({ title, text, icon: Icon }) => (
                <div
                  key={title}
                  className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm"
                >
                  <div className="mb-5 flex h-11 w-11 items-center justify-center rounded-xl bg-sky-50 text-sky-600">
                    <Icon className="h-5 w-5" />
                  </div>
                  <h3 className="font-poppins text-lg font-bold text-slate-900">{title}</h3>
                  <p className="mt-2 text-sm leading-6 text-slate-600">{text}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section id="sa-fungerar-det" className="mx-auto max-w-7xl px-5 py-16 sm:py-20 lg:px-8">
          <div className="mx-auto mb-12 max-w-2xl text-center">
            <p className="text-xs font-bold uppercase tracking-[0.18em] text-sky-700">
              Från beställning till rapport
            </p>
            <h2 className="mt-3 font-poppins text-3xl font-bold tracking-tight text-slate-950 sm:text-4xl">
              Så enkelt fungerar det
            </h2>
            <p className="mt-4 leading-7 text-slate-600">
              Beställ värderingen via Kronos och följ stegen tills du får din rapport.
            </p>
          </div>
          <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
            {steps.map(({ title, text, icon: Icon }, index) => (
              <div
                key={title}
                className="relative rounded-2xl border border-slate-200 bg-white p-6 shadow-sm transition hover:-translate-y-1 hover:border-sky-200 hover:shadow-lg"
              >
                <div className="mb-5 flex items-center justify-between">
                  <span className="flex h-10 w-10 items-center justify-center rounded-full bg-sky-600 text-sm font-bold text-white">
                    {index + 1}
                  </span>
                  <Icon className="h-6 w-6 text-sky-600" />
                </div>
                <h3 className="font-poppins text-base font-bold text-slate-900">{title}</h3>
                <p className="mt-2 text-sm leading-6 text-slate-600">{text}</p>
              </div>
            ))}
          </div>
        </section>

        <section className="px-5 pb-16 lg:px-8">
          <div className="mx-auto flex max-w-5xl flex-col items-center justify-between gap-8 rounded-3xl border border-sky-100 bg-gradient-to-br from-sky-50 to-white p-7 text-center shadow-sm sm:p-10 md:flex-row md:text-left">
            <div>
              <p className="text-xs font-bold uppercase tracking-[0.16em] text-sky-700">
                Pris för distansvärdering
              </p>
              <h2 className="mt-2 font-poppins text-2xl font-bold text-slate-950 sm:text-3xl">
                Värdera din klocka
              </h2>
              <p className="mt-3 text-sm leading-6 text-slate-600">
                Ordinarie pris{' '}
                <span className="font-semibold text-slate-500 line-through">1 500 kr</span>
              </p>
              <p className="mt-1 text-base text-slate-700">
                Kronos medlemspris <span className="text-2xl font-bold text-sky-700">1 250 kr</span>
              </p>
              <p className="mt-2 text-xs text-slate-500">
                Logga in på Kronos för att ta del av medlemspriset.
              </p>
            </div>
            <a
              href="/"
              className="inline-flex w-full items-center justify-center gap-2 rounded-xl bg-sky-600 px-6 py-3.5 text-sm font-bold text-white shadow-lg shadow-sky-600/20 transition hover:bg-sky-700 sm:w-auto"
            >
              Gå till Kronos <ArrowRightIcon className="h-4 w-4" />
            </a>
          </div>
        </section>

        <section className="border-t border-slate-200 bg-white px-5 py-12 text-center lg:px-8">
          <div className="mx-auto max-w-2xl">
            <h2 className="font-poppins text-2xl font-bold text-slate-900">
              Har du frågor om klockvärdering?
            </h2>
            <p className="mt-3 text-slate-600">Kontakta oss så hjälper vi dig vidare.</p>
            <a
              href="mailto:info@kronos.se"
              className="mt-5 inline-flex items-center gap-2 font-semibold text-sky-700 hover:text-sky-800"
            >
              info@kronos.se <ArrowRightIcon className="h-4 w-4" />
            </a>
          </div>
        </section>
      </main>
    </div>
  )
}

export default Klockvardering
